import { t } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import type { ResumeDto } from "@reactive-resume/dto";
import { ResumeVisibility } from "@reactive-resume/dto";
import { defaultResumeData } from "@reactive-resume/schema";
import { templatesList } from "@reactive-resume/utils";
import slugify from "@sindresorhus/slugify";
import { useCallback, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import type { LoaderFunction } from "react-router";
import { redirect } from "react-router";

import { TEMPORARY_USER_ID } from "@/client/constants/resume";
import { RESUME_DRAFT_KEY } from "@/client/constants/storage-keys";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";

export const NewBuilderPage = () => {
  const frameRef = useBuilderStore((state) => state.frame.ref);
  const setFrameRef = useBuilderStore((state) => state.frame.setRef);

  const resume = useResumeStore((state) => state.resume);
  const title = useResumeStore((state) => state.resume.title);

  const syncResumeToArtboard = useCallback(() => {
    setImmediate(() => {
      if (!frameRef?.contentWindow) return;
      const message = { type: "SET_RESUME", payload: resume.data };
      frameRef.contentWindow.postMessage(message, "*");
    });
  }, [frameRef?.contentWindow, resume.data]);

  // Send resume data to iframe on initial load
  useEffect(() => {
    if (!frameRef) return;

    frameRef.addEventListener("load", syncResumeToArtboard);

    return () => {
      frameRef.removeEventListener("load", syncResumeToArtboard);
    };
  }, [frameRef, syncResumeToArtboard]);

  // Persistently check if iframe has loaded using setInterval
  useEffect(() => {
    const interval = setInterval(() => {
      if (frameRef?.contentWindow?.document.readyState === "complete") {
        syncResumeToArtboard();
        clearInterval(interval);
      }
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [frameRef, syncResumeToArtboard]);

  // Send resume data to iframe on change of resume data
  useEffect(syncResumeToArtboard, [resume.data]);

  return (
    <>
      <Helmet>
        <title>
          {title || t`Untitled Resume`} - {t`Resumotion`}
        </title>
      </Helmet>

      <iframe
        ref={setFrameRef}
        title={resume.id || "new-resume"}
        src="/artboard/builder"
        className="mt-16 w-screen"
        style={{ height: `calc(100vh - 64px)` }}
      />
    </>
  );
};

const loadResumeFromLocalStorage = (): ResumeDto | null => {
  try {
    const savedResumeJson = localStorage.getItem(RESUME_DRAFT_KEY);
    if (savedResumeJson) {
      const savedResume = JSON.parse(savedResumeJson) as ResumeDto;
      
      // Validate that the saved resume has required fields
      if (savedResume && savedResume.id && savedResume.data) {
        useResumeStore.setState({ resume: savedResume });
        useResumeStore.temporal.getState().clear();
        
        localStorage.removeItem(RESUME_DRAFT_KEY);
        return savedResume;
      }
    }
  } catch (error) {
    console.error("Failed to load resume from localStorage:", error);
  }
  
  return null;
};

export const newBuilderLoader: LoaderFunction<ResumeDto> = async ({ request }) => {
  // Check localStorage for saved resume draft
  const savedResume = loadResumeFromLocalStorage();
  if (savedResume) {
    return savedResume;
  }

  const url = new URL(request.url);
  const template = url.searchParams.get("template");

  // Validate template
  if (!template || !templatesList.includes(template as (typeof templatesList)[number])) {
    const defaultTemplate = templatesList[0];
    url.searchParams.set("template", defaultTemplate);
    return redirect(url.pathname + "?" + url.searchParams.toString());
  }

  const tempId = createId();
  const tempTitle = t`Untitled Resume`;
  const tempSlug = slugify(tempTitle);

  const resumeData = {
    ...defaultResumeData,
    metadata: {
      ...defaultResumeData.metadata,
      template: template as (typeof templatesList)[number],
    },
  };

  const resume: ResumeDto = {
    id: tempId,
    title: tempTitle,
    slug: tempSlug,
    data: resumeData,
    visibility: ResumeVisibility.Private,
    locked: false,
    userId: TEMPORARY_USER_ID,
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  // Initialize resume store
  useResumeStore.setState({ resume });
  useResumeStore.temporal.getState().clear();

  return resume;
};

